import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Info, Shield, AlertCircle } from 'lucide-react';
import { Button } from './Button';
interface DepositPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  depositAmount: number;
}
export function DepositPolicyModal({
  isOpen,
  onClose,
  depositAmount
}: DepositPolicyModalProps) {
  return <AnimatePresence data-id="element-203">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" data-id="element-204" />

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[85vh]" data-id="element-205">
            <div className="flex items-center justify-between px-6 py-4 border-b border-sand/50" data-id="element-206">
              <h2 className="text-xl font-bold text-charcoal" data-id="element-207">
                Deposit (hold)
              </h2>
              <button onClick={onClose} className="p-2 hover:bg-sand/30 rounded-full transition-colors" data-id="element-208">
                <X size={24} className="text-charcoal" data-id="element-209" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-6 space-y-6" style={{
          maxHeight: 'calc(85vh - 140px)'
        }} data-id="element-210">
              {/* Summary */}
              <div className="bg-sky-blue/10 border border-sky-blue/30 rounded-ios-lg p-4" data-id="element-211">
                <div className="flex items-start gap-3" data-id="element-212">
                  <div className="w-10 h-10 rounded-full bg-sky-blue/20 flex items-center justify-center flex-shrink-0" data-id="element-213">
                    <Info size={20} className="text-dark-green" data-id="element-214" />
                  </div>
                  <div data-id="element-215">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-216">
                      ${depositAmount} Hold on Your Card
                    </h3>
                    <p className="text-sm text-charcoal/80 leading-relaxed" data-id="element-217">
                      Rather than dealing with insurance policies, if you borrow
                      an item, a hold is placed on your credit card. This amount
                      is determined by the lender.
                    </p>
                  </div>
                </div>
              </div>

              {/* How it Works */}
              <div className="space-y-4" data-id="element-218">
                <h3 className="font-semibold text-charcoal text-lg" data-id="element-219">
                  How it works
                </h3>

                <div className="space-y-3" data-id="element-220">
                  <div className="flex gap-3" data-id="element-221">
                    <div className="w-8 h-8 rounded-full bg-dark-green/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-222">
                      <span className="text-sm font-bold text-dark-green" data-id="element-223">
                        1
                      </span>
                    </div>
                    <div data-id="element-224">
                      <p className="text-sm text-charcoal leading-relaxed" data-id="element-225">
                        <span className="font-medium" data-id="element-226">When you book:</span> A
                        temporary hold is placed on your credit card for the
                        deposit amount.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3" data-id="element-227">
                    <div className="w-8 h-8 rounded-full bg-dark-green/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-228">
                      <span className="text-sm font-bold text-dark-green" data-id="element-229">
                        2
                      </span>
                    </div>
                    <div data-id="element-230">
                      <p className="text-sm text-charcoal leading-relaxed" data-id="element-231">
                        <span className="font-medium" data-id="element-232">During rental:</span> The
                        hold remains on your card. No charges are made unless
                        there's damage.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3" data-id="element-233">
                    <div className="w-8 h-8 rounded-full bg-dark-green/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-234">
                      <span className="text-sm font-bold text-dark-green" data-id="element-235">
                        3
                      </span>
                    </div>
                    <div data-id="element-236">
                      <p className="text-sm text-charcoal leading-relaxed" data-id="element-237">
                        <span className="font-medium" data-id="element-238">Upon return:</span> The
                        lender has 7 days to file a claim if their item is not
                        returned in the condition it was lent out in.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3" data-id="element-239">
                    <div className="w-8 h-8 rounded-full bg-dark-green/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-240">
                      <span className="text-sm font-bold text-dark-green" data-id="element-241">
                        4
                      </span>
                    </div>
                    <div data-id="element-242">
                      <p className="text-sm text-charcoal leading-relaxed" data-id="element-243">
                        <span className="font-medium" data-id="element-244">Resolution:</span> If a
                        claim is substantiated by the AllHere Support Team, the
                        full or partial deposit hold is charged to you and given
                        to the lender.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Important Note */}
              <div className="bg-clay/10 border border-clay/30 rounded-ios-lg p-4" data-id="element-245">
                <div className="flex items-start gap-3" data-id="element-246">
                  <AlertCircle size={20} className="text-clay flex-shrink-0 mt-0.5" data-id="element-247" />
                  <div data-id="element-248">
                    <h4 className="font-semibold text-charcoal mb-1" data-id="element-249">
                      Important
                    </h4>
                    <p className="text-sm text-charcoal/80 leading-relaxed" data-id="element-250">
                      The deposit hold is released automatically if no claim is
                      filed within 7 days of return. You will not be charged
                      unless damage is confirmed.
                    </p>
                  </div>
                </div>
              </div>

              {/* Full Policy Link */}
              <button className="w-full text-center text-sm font-medium text-dark-green hover:underline" data-id="element-251">
                Read Full Deposit Policy
              </button>
            </div>

            <div className="px-6 py-4 border-t border-sand/50" data-id="element-252">
              <Button variant="primary" fullWidth onClick={onClose} data-id="element-253">
                Got it
              </Button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}